<template>
    <Dialog :visible="showDialog" class="action-popup registration-done-popup" :modal="true" :draggable="false">
        <template #container>

            <div class="dialog-header flex flex-row">
                <div style="flex: 1;"></div>
                <div style="flex: 5;" class="flex flex-row align-items-center justify-content-center">Confirm Your Account!</div>
                <div style="flex: 1;" class="flex flex-row align-items-center justify-content-end">
                    <SvgCross class="cursor-pointer" className="table-licenses-svg" color="var(--button-normal-color)" @click="close()"/>
                </div>
            </div>
            <div class="dialog-content align-items-center text-center pt-4" style="padding-bottom: 1.2rem;">
                <span class="text mb-3 sm:px-5 px-1">A confirmation link has been sent to <span style="font-weight: var(--font-weight-bold);">{{ account.email }}</span>.</span>
                <span class="text"> Please check your inbox.</span>
            </div>
            <div class="dialog-footer flex flex-column mt-0 pt-0 pb-4 text-center">
                <span class="text mb-4">Did not receive the email? Check your spams or wait a few minutes.</span>
                <span class="text px-2 sm:px-8 text-center mb-1" style="font-style: italic; color: var(--inputcomponent-invalid-color);" v-if="emailSendingLimitText">{{ emailSendingLimitText }}</span>
                <span class="text mb-1" style="font-style: italic; color: var(--mysuccess-color);" v-if="emailSentAgainText" >{{ emailSentAgainText }}</span>

                <div v-if="!httpSendAgainLoading">
                    <span v-if="canSendAgain" id="send-again-text" class="text cursor-pointer" @click="sendAgain">Send email again</span>
                </div>
                <div v-else>
                    <span class="text" style="opacity: 0.65;">Wait ...</span>
                </div>
            </div>

        </template>
    </Dialog>
</template>

<script setup>
// --- IMPORT ---
import { ref, toRefs, watchEffect, onMounted, onBeforeUnmount } from 'vue';
// PrimeVue
import Dialog from 'primevue/dialog';
// vue
import SvgCross from '@/components/SVGs/SvgCross.vue';
// Others
import InputsUtils from '@/composables/InputsUtils'
import toastService from '@/composables/toastService';

// API
import useApiRequest from '@/composables/HTTPS/useApiRequest';
const { fetchSendAgain } = useApiRequest()
const httpSendAgainLoading = ref(false)
const httpSendAgainData = ref(null)

// --- EMITS ---
const emit = defineEmits(['close'])

// --- PROPS ---
const props = defineProps({
    showDialog: Boolean,
    account: Object,
    invitation: {
        type: String,
        default: null
    }
})
const { showDialog, account, invitation } = toRefs(props);

// --- Close ---
const close = () => {
    emailSentAgainText.value = null
    emailSendingLimitText.value = null
    emit('close');
}

// --- SEND EMAIL AGAIN ---
const canSendAgain = ref(true)
const emailSentAgainText = ref(null)
const emailSendingLimitText = ref(null)
const sendAgain = async () => {
    // Send HTTP request
    httpSendAgainLoading.value = true
    try {
        if (invitation.value) {
            httpSendAgainData.value = await fetchSendAgain({ type: 'register', email: account.value.email, invitation: invitation.value })
        } else {
            httpSendAgainData.value = await fetchSendAgain({ type: 'register', email: account.value.email })
        }
        emailSendingLimitText.value = null
        emailSentAgainText.value = "A new confirmation email has been sent."
    } catch (err) {
        handleSendEmailError(err)
    } finally {
        httpSendAgainLoading.value = false
    }
}
// Handle an error from the server
const handleSendEmailError = (error) => {
    try {
        if (error.response == null) { // If there is no .response key, then it means it is not an error from the server, it did not reach it
            toastService.add({ severity: 'error', summary: `Register: Server not reachable.`, detail: `The server is not reachable. Please make sure you are connected to the internet. If you are, please try again.`, life: 5000 });
            return;
        }
        const errorStatus = error.response.status
        const errorCode = error.response.data.error_code
        const errorMessage = error.response.data.error_message
        switch (errorStatus) {
            case 400: // Missing info = MISSING_OBJ / MISSING_OBJ_ELEM (400) => toast
                if (errorCode == 'MISSING_OBJ' || errorCode == 'MISSING_OBJ_ELEM') {
                    toastService.add({ severity: 'warn', summary: "Register: Missing Information.", detail: 'It seems that the request body does not contain the necessary information.', life: 5000 });
                }
                else {  toastService.add({ severity: 'error', summary: `Register: Error ${errorStatus}.`, detail: errorMessage, life: 12000 }); }
                break;
            case 401: // Too much emails sent = EMAIL_SENDING_LIMIT_REACHED (401) => toast
                if (errorCode == 'EMAIL_SENDING_LIMIT_REACHED') {
                    emailSentAgainText.value = null
                    emailSendingLimitText.value = "The confirmation email has been sent very recently. Please wait a few moment before trying to send the email again."
                }
                else {  toastService.add({ severity: 'error', summary: `Register: Error ${errorStatus}.`, detail: errorMessage, life: 12000 }); }
                break;
            case 404: // ACCOUNT_NOT_FOUND (404) => toast
                if (errorCode == 'ACCOUNT_NOT_FOUND') {
                    emailSentAgainText.value = null
                    // toastService.add({ severity: 'warn', summary: "Register: Account Not Found.", detail: 'There is no registered account for this email. Please make a new registration.', life: 5000 });
                    emailSendingLimitText.value = "There is no registered account for this email. Please make a new registration."
                    canSendAgain.value = false
                }
                else {  toastService.add({ severity: 'error', summary: `Register: Error ${errorStatus}.`, detail: errorMessage, life: 12000 }); }
                break;
            case 409: // Account already register = ACCOUNT_ALREADY_CONFIRMED (409) => if here, it means it has been validated
                if (errorCode == 'ACCOUNT_ALREADY_CONFIRMED') {
                    emailSentAgainText.value = null
                    //toastService.add({ severity: 'warn', summary: "Register: Account Already Confirmed.", detail: 'It seems that the account is already confirmed.', life: 5000 });
                    emailSendingLimitText.value = "It seems that the account is already registered and confirmed. There is no need to send a new confirmation link."
                    canSendAgain.value = false
                }
                else {  toastService.add({ severity: 'error', summary: `Register: Error ${errorStatus}.`, detail: errorMessage, life: 12000 }); }
                break;
            case 500: // DATABASE_ERROR / EMAIL_ERROR (500) => toast
                if (error.response.data.error_code == 'EMAIL_ERROR') {
                    toastService.add({ severity: 'error', summary: "Register: Email Sending.", detail: 'Our server did not manage sending the confirmation email to the address you provided. Make sure it is a valid address email.', life: 5000 });
                }
                else if (errorCode == 'DATABASE_ERROR') {
                    toastService.add({ severity: 'error', summary: "Register: Internal Error.", detail: 'Sorry, it seems that there is an internal error. Try later.', life: 5000 });
                }
                else {
                    toastService.add({ severity: 'error', summary: `Register: Error ${errorStatus}.`, detail: errorMessage, life: 12000 });
                }
                break;
            default:
                toastService.add({ severity: 'error', summary: `Register: Error ${errorStatus}.`, detail: errorMessage, life: 12000 });
                break;
        }
    } catch {
        toastService.add({ severity: 'error', summary: `Register: Server not reachable.`, detail: `The server is not reachable. Please make sure you are connected to the internet. If you are, please try again.`, life: 5000 });
    }
}
</script>

<style scoped>
/* Forgot password */
#send-again-text {
    text-decoration: none;
    color: var(--button-normal-color);
    border-bottom: 1px solid transparent;
}
#send-again-text:hover {
    border-bottom: 1px solid var(--button-normal-color);
}
</style>